<template>
  <iColumn
    class="event-join-card"
    width="fill"
    height="fill"
    gap="none"
    align="center"
    vertical-align="middle"
    wrap="nowrap"
    :style-overrides="{
      paddingTop: 'large',
      paddingRight: 'large',
      paddingBottom: 'large',
      paddingLeft: 'large',
    }"
  >
    <iColumn
      width="fill"
      gap="none"
      wrap="nowrap"
      overflow="hidden"
      :max-width="760"
      :style-overrides="{
        backgroundColor: 'paper',
        borderRadius: 'large',
        borderWidth: 'thin',
        borderColor: 'light',
      }"
    >
      <iImageContainer
        v-if="coverImage"
        :src="coverImage"
        width="fill"
        :height="260"
        crop-if-needed
        whitespace-style="blur"
        data-test="event-join-cover"
      >
        <template #overlay>
          <iRow
            width="fill"
            align="left"
            :style-overrides="{
              paddingTop: 'standard',
              paddingLeft: 'standard',
            }"
          >
            <iBadge
              v-if="status && roomAppearance.showStatusChip"
              :label="status"
              :variant="statusVariant"
              size="small"
              data-test="event-join-status"
            />
          </iRow>
        </template>
      </iImageContainer>
      <iColumn
        gap="large"
        width="fill"
        wrap="nowrap"
        :style-overrides="{
          paddingTop: 'large',
          paddingRight: 'large',
          paddingBottom: 'large',
          paddingLeft: 'large',
        }"
      >
        <iColumn gap="extraSmall" width="fill">
          <iRow
            v-if="!coverImage && status && roomAppearance.showStatusChip"
            width="hug"
            :style-overrides="{paddingBottom: 'small'}"
          >
            <iBadge
              :label="status"
              :variant="statusVariant"
              size="small"
              data-test="event-join-status"
            />
          </iRow>
          <iText
            font-size="verySmall"
            font-weight="dark"
            variant="subtle"
            text-case="uppercase"
            data-test="event-join-when"
          >
            {{ eyebrow }}
          </iText>
          <iHeading data-test="event-join-title">
            {{ eventTitle || title }}
          </iHeading>
          <iText v-if="eventTitle && title && title !== eventTitle" font-weight="dark">
            {{ title }}
          </iText>
          <iText v-if="subtitle" variant="subtle">
            {{ subtitle }}
          </iText>
        </iColumn>

        <iRow
          v-if="hostName"
          gap="small"
          vertical-align="middle"
          wrap="nowrap"
          data-test="event-join-host"
        >
          <iAvatar :name="hostName" :src="hostLogo" size="small" />
          <iText font-size="small">
            Hosted by {{ hostName }}
          </iText>
        </iRow>

        <iRow
          v-if="countdownSegments"
          gap="large"
          vertical-align="middle"
          wrap="nowrap"
          data-test="event-join-countdown"
        >
          <iStat v-if="countdownSegments.days > 0" :value="countdownSegments.days" label="days" />
          <iStat :value="countdownSegments.hours" label="hours" />
          <iStat :value="countdownSegments.minutes" label="minutes" />
          <iStat v-if="countdownSegments.days === 0" :value="countdownSegments.seconds" label="seconds" />
        </iRow>

        <iRow
          v-if="roomAppearance.showViewerCount && presenceCopy"
          gap="small"
          vertical-align="middle"
          wrap="nowrap"
        >
          <iAvatarStack
            v-if="peopleStack.length"
            :people="peopleStack"
            size="small"
            data-test="event-join-people"
          />
          <iText font-size="small" data-test="event-join-presence">
            {{ presenceCopy }}
          </iText>
        </iRow>

        <iRow
          gap="standard"
          vertical-align="middle"
          wrap="wrap"
        >
          <iButton
            v-if="showJoin"
            variant="primary"
            size="large"
            :disabled="!canJoin"
            data-test="event-join-button"
            @click="$emit('join')"
          >
            {{ joinLabel }}
          </iButton>
          <iButton
            v-if="inviteLink"
            variant="text"
            data-test="event-join-invite"
            @click="copyInviteLink"
          >
            {{ copiedInviteLink ? "Link copied" : "Copy invite link" }}
          </iButton>
        </iRow>
      </iColumn>
    </iColumn>
  </iColumn>
</template>

<script>
import { RoomAppearanceMixin } from "%/apps/video-engine/room-appearance";
import { stackPeople } from "%/apps/video-engine/room-context";
import { describePresence } from "%/dynamic-component-editor/helpers/event-presence";

export default {
  name: "EventJoinCard",
  mixins: [RoomAppearanceMixin],
  props: {
    title: {
      type: String,
      required: false,
      default: "Join the event",
    },
    eventTitle: {
      type: String,
      required: false,
      default: "",
    },
    subtitle: {
      type: String,
      required: false,
      default: "",
    },
    status: {
      type: String,
      required: false,
      default: "",
    },
    coverImage: {
      type: String,
      required: false,
      default: "",
    },
    whenCopy: {
      type: String,
      required: false,
      default: "",
    },
    hostName: {
      type: String,
      required: false,
      default: "",
    },
    hostLogo: {
      type: String,
      required: false,
      default: "",
    },
    people: {
      type: Array,
      required: false,
      default: () => [],
    },
    countdownSegments: {
      type: Object,
      required: false,
      default: null,
    },
    joinLabel: {
      type: String,
      required: false,
      default: "Join",
    },
    canJoin: {
      type: Boolean,
      required: false,
      default: true,
    },
    showJoin: {
      type: Boolean,
      required: false,
      default: true,
    },
    participantCount: {
      type: Number,
      required: false,
      default: 0,
    },
    isLivestream: {
      type: Boolean,
      required: false,
      default: false,
    },
    inviteLink: {
      type: String,
      required: false,
      default: "",
    },
  },
  emits: ["join"],
  data() {
    return {
      copiedInviteLink: false,
    };
  },
  computed: {
    statusVariant() {
      return /^(live|in progress)/i.test(this.status) ? "success" : "standard";
    },
    eyebrow() {
      const kind = this.isLivestream ? "Livestream" : "Meeting";

      return this.whenCopy ? `${kind} · ${this.whenCopy}` : kind;
    },
    peopleStack() {
      return stackPeople(this.people);
    },
    presenceCopy() {
      if (!this.canJoin) {
        return "";
      }

      return describePresence(this.participantCount, this.isLivestream, { beforeJoin: true });
    },
  },
  methods: {
    copyInviteLink() {
      if (!navigator.clipboard || !window.isSecureContext) {
        return;
      }
      navigator.clipboard.writeText(this.inviteLink).then(() => {
        this.copiedInviteLink = true;
      });
    },
  },
};
</script>
